"use client";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import router from "next/router";
import { getCookie, getCookies, deleteCookie, setCookie } from "cookies-next";

import useMediaQuery from "@/utils/hooks/useMediaQuery";
import UserInfo from "../../UserInfo";
import ToastComponent from "@/components/Toast/Toast";
import {
  useAppDispatch,
  useAppSelector,
} from "@/utils/hooks/reactUseCustomHooks";
import useInputIntent from "@/utils/hooks/useInputIntent";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { handleEmailLogin } from "@/helpers/useCommonFunctions";
import { destroyCartDB } from "@/lib/indexedDb/product";
import {
  fetchMulesoftOffers,
  addOffersToList,
  mulesoftLoadOffersService,
  mulesoftMemberInquireService,
  getOffersFromList,
  deleteOffersFromList,
} from "@/services/mulesoft";
import { BB_OFFER_TYPE } from "@/utils/constant";
import { setFavoirateOffers } from "@/utils/redux/favOffers";
import {
  setLoadingOffers,
  setLoadedOffers,
  setHashOffers,
  resetLoadedOffers,
} from "@/utils/redux/fetchOffers";
import { setAlert } from "@/utils/redux/gigya/scene";
import { setSceneContent } from "@/utils/redux/scene/sceneContentSlice";
import UserPoints from "../../UserPoints";
import LoadedOffersToast from "../../LoadedOffersToast";

const COOKIE_RESET_KEY = "cookieResetKey";
const CART_RESET_KEY = "cartResetKey";

export default function MegaBar({
  sceneContentData,
  headerData
}: {
  sceneContentData: any;
  headerData:any
}) {
  const mobileMedia: number = useMediaQuery() || 0;
  const searchParams = useSearchParams();
  const { scene, uid } = useAppSelector((state) => state.gigyaslice);
  const mulesoftOffers = useAppSelector((state) => state?.offersSlice);
  const { favOffers } = useAppSelector((state) => state.favoirateOffersSlice);
  const dispatch = useAppDispatch();

  const [isOfferLoaded, setIsOfferLoaded] = useState(false);
  const [emailLoginCard, setEmailLoginCard] = useState<string | null>(null);
  const [mulesoftPendingOffers, setMulesoftPendingOffers] = useState<any[]>([]);
  const [cardStatus, setCardStatus] = useState("");
  const [bbOffers, setBBOffers]= useState<any>([]);

  const isHash = useMemo(
    () => [...searchParams.keys()].includes("hash"),
    [searchParams]
  );
  const hashValue = useMemo(
    () => searchParams.get("hash") ?? "",
    [searchParams]
  );

  useInputIntent(); // This hook doesn't seem to return anything used here, so its impact on performance is minimal unless it's doing heavy DOM manipulation.

  const gigyaToken = useGigyaAccountInfo();

  // Memoize getToken to prevent unnecessary re-creations
  const getToken = useCallback(async () => {
    const result = await gigyaToken();
    return result?.idToken ?? "";
  }, [gigyaToken]);

  const enrollmentStatus = useMemo(() => {
    return scene?.enrollmentStatus ? scene?.enrollmentStatus : cardStatus;
  }, [scene, cardStatus]);

  const cardNumber = useMemo(() => {
    return isHash ? emailLoginCard : scene?.cardNumber;
  }, [isHash, emailLoginCard, scene?.cardNumber]);

  const allOffersService = useCallback(async () => {
    try {
      const token = !isHash ? await getToken() : "";
      const allOffersResponse = await fetchMulesoftOffers({
        key: "allOffers",
        cardNumber,
        token,
        isHash,
      });

      if (allOffersResponse?.response?.activeOffers.length > 0) {
        const allOffers = [
          ...(allOffersResponse?.response?.activeOffers || []),
          ...(allOffersResponse?.response?.pendingOffers || []),
        ];
        const basketBuilderOffers = allOffers?.filter(
          (elem: any) =>
            BB_OFFER_TYPE.includes(parseInt(elem.offerType)) &&
            elem?.bannerInd?.includes(process.env.NEXT_PUBLIC_BANNER)
        );

        const bbData = basketBuilderOffers.map((item: any) => ({
          offer_id: item.offerId,
          banner: item.bannerInd,
          offer_type: item.offerType,
          offer_description: JSON.stringify(item),
        }));

        setBBOffers(bbData);

        // const existingBbOfferIds = new Set(
        //   favOffers?.basketBuilderOffers?.map((elem: any) => elem.offer_id)
        // );

        // const newBbOffers = bbData.filter(
        //   (offer: any) => !existingBbOfferIds.has(offer.offer_id)
        // );
        // if(!isHash){
        //   if (newBbOffers.length > 0) {
        //     await addOffersToList(newBbOffers, token);
        //   }
        // }
      }
      return allOffersResponse;
    } catch (error) {
      console.error("Error fetching Mulesoft offers:", error);
      return null;
    }
  }, [cardNumber, isHash, getToken, favOffers?.basketBuilderOffers]); // Added favOffers to dependencies

  const loadOffersService = useCallback(
    async (pendingOffers: any[]) => {
      try {
        await mulesoftLoadOffersService({
          key: "loadOffers",
          cardNumber,
          userId: (uid as string) ?? "member",
          offers: pendingOffers,
          token: !isHash ? await getToken() : "",
          isHash: isHash,
        });
      } catch (error) {
        console.error("Error loadOffers:", error);
      }
    },
    [cardNumber, uid, isHash, getToken]
  );

  const memberInquireService = useCallback(
    async ({ currentCardStatus }: { currentCardStatus: string }) => {
      dispatch(setLoadingOffers(true));
      try {
        let inquireResponse: any = null;
        if (isHash) {
          inquireResponse = await mulesoftMemberInquireService({
            key: "memberInquire",
            cardNumber,
            token: "",
            isHash: isHash,
          });
          if (inquireResponse?.response?.enrollmentStatus) {
            setCardStatus(inquireResponse?.response?.enrollmentStatus);
          }
        }

        const effectiveCardStatus =
          inquireResponse?.response?.enrollmentStatus || currentCardStatus;

        if (effectiveCardStatus !== "LP00") {
          const allOffersResponse = await allOffersService();
          const pendingOffersList =
            allOffersResponse?.response?.pendingOffers?.filter((offer: any) =>
              offer?.bannerInd?.includes(process.env.NEXT_PUBLIC_BANNER)
            ) || [];

          if (pendingOffersList.length > 0) {
            const pendingOffers = pendingOffersList.map((offer: any) => ({
              id: offer.id,
              channel: isHash ? "001" : "004",
              banner: process.env.NEXT_PUBLIC_BANNER_CODE ?? "005",
            }));
            setMulesoftPendingOffers(pendingOffers);
            await loadOffersService(pendingOffers);
            setIsOfferLoaded(true);
          }

          if (allOffersResponse?.response) {
            dispatch(
              setLoadedOffers({
                response: {
                  activeOffers: [
                    ...pendingOffersList,
                    ...(allOffersResponse?.response?.activeOffers || []),
                  ],
                  expiredOffers: [
                    ...(allOffersResponse?.response?.expiredOffers || []),
                  ],
                  redeemedOffers: [
                    ...(allOffersResponse?.response?.redeemedOffers || []),
                  ],
                },
              })
            );
            if (isHash) {
              dispatch(setHashOffers(true));
            }
          } else {
            dispatch(setLoadedOffers(allOffersResponse));
          }
        }
      } catch (error) {
        console.error("Error in memberInquireService:", error);
      } finally {
        dispatch(setLoadingOffers(false));
      }
    },
    [
      isHash,
      cardNumber,
      allOffersService,
      loadOffersService,
      dispatch,
      // enrollmentStatus is passed as currentCardStatus
    ]
  );

  // Effect to reset loaded offers if hash changes
  useEffect(() => {
    if (!isHash && mulesoftOffers?.isHash) {
      dispatch(resetLoadedOffers());
    }
  }, [isHash, mulesoftOffers?.isHash, dispatch]);

  // Effect to handle email login via hash
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    const handleLogin = async () => {
      if (isHash && hashValue) {
        const emailRes = await handleEmailLogin(hashValue, isHash);
        if (emailRes?.cardNumber) {
          setEmailLoginCard(emailRes?.cardNumber);
        } else {
          dispatch(
            setAlert({
              message:
                "The link has expired, please sign in to view your current offers or contact Scene+ support at 1-866-586-2805.",
              color: "error",
              isError: true,
            })
          );
          timer = setTimeout(() => {
            router.push("/");
          }, 3000);
        }
      }
    };
    handleLogin();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isHash, hashValue, dispatch]); // Dependencies added

  // Effect to fetch offers when cardNumber or enrollmentStatus changes
  useEffect(() => {
    if (cardNumber) {
      memberInquireService({ currentCardStatus: enrollmentStatus });
    } else {
      dispatch(setLoadingOffers(false));
    }
  }, [cardNumber, enrollmentStatus]);

  // Effect to dispatch favorite offers
  useEffect(() => {
    const dispatchFavOffers = async () => {
      if (!uid || !cardNumber) {
        // No need to dispatch if user not logged in or no card
        dispatch(
          setFavoirateOffers({ favouriteOffers: [], basketBuilderOffers: [] })
        );
        return;
      }

      const token = await getToken();
      if (!token) return; // Exit if no token

      let favOffersData: any = null;
      try {
        const data = await getOffersFromList({
          preferredLanguage: "en",
          token,
        });
        favOffersData = data?.response;
      } catch (error) {
        console.error("Error fetching fav offers list:", error);
        return;
      }

      // To check redeemed offers and update our database accordingly
      const redeemedOffers = mulesoftOffers?.redeemedOffers || [];
      const expiredOffers = mulesoftOffers?.expiredOffers || [];

      if (redeemedOffers.length > 0 || expiredOffers.length > 0) {
        const allRemoteFavOffers = [
          ...(favOffersData?.basketBuilderOffers || []),
          ...(favOffersData?.favouriteOffers || []),
        ];
        const favOffersId = new Set(
          allRemoteFavOffers?.map((r: any) => r.offer_id)
        );
        const redeemedOffersId = new Set(
          redeemedOffers?.map((r: any) => r.offerId)
        );
        const expiredOffersId = new Set(
          expiredOffers?.map((r: any) => r.offerId)
        );

        // Find common offer IDs to delete
        const commonOfferIdsToDelete: string[] = [];
        favOffersId.forEach((id) => {
          if (redeemedOffersId.has(id) || expiredOffersId.has(id)) {
            commonOfferIdsToDelete.push(id);
          }
        });

        // Delete offers in parallel
        await Promise.all(
          commonOfferIdsToDelete.map((elem) =>
            deleteOffersFromList({ offer_id: elem, token })
          )
        );

        // Re-fetch updated fav offers after deletion
        try {
          const updatedData = await getOffersFromList({
            preferredLanguage: "en",
            token,
          });
          favOffersData = updatedData?.response;
        } catch (error) {
          console.error("Error re-fetching fav offers after deletion:", error);
          return;
        }
      }

      const favOfferIds = new Set(
        favOffersData?.favouriteOffers?.map((item: any) => item.offer_id)
      );
      const filteredOffers = mulesoftOffers?.activeOffers?.filter((item) =>
        favOfferIds.has(item.offerId)
      );
      const filteredFavOffersMapped = filteredOffers.map((itm) => {
        return {
          offer_id: itm.offerId,
          banner: itm.banner,
          offer_type: itm.offerType,
          uid: itm.uid,
          offer_description: JSON.stringify(itm),
        };
      });

      dispatch(
        setFavoirateOffers({
          favouriteOffers: filteredFavOffersMapped,
         // basketBuilderOffers: favOffersData?.basketBuilderOffers || [],
         basketBuilderOffers:  bbOffers || []
        })
      );
    };

    dispatchFavOffers();
  }, [
    uid,
    cardNumber,
    getToken,
    mulesoftOffers?.redeemedOffers,
    mulesoftOffers?.expiredOffers,
    mulesoftOffers?.activeOffers,
    dispatch,
  ]);
  useEffect(() => {
    const processSceneContent = async () => {
      try {
        const items = sceneContentData?.data?.imageAndContentModelList?.items;
        if (!items || items.length === 0) return;

        const { cookieReset: cookieResetFromAEM, cartReset: cartResetFromAEM } =
          items[0] || {};

        // Handle Cookie Reset
        if (cookieResetFromAEM) {
          const cookieValue = getCookie(COOKIE_RESET_KEY);
          if (cookieValue !== cookieResetFromAEM) {
            const allCookies: any = getCookies();
            await Promise.all(
              Object.keys(allCookies).map((key) => deleteCookie(key))
            );
            localStorage.clear();
            sessionStorage.clear();
            setCookie(COOKIE_RESET_KEY, cookieResetFromAEM, {
              maxAge: 60 * 60 * 24 * 365 * 3, // 3 years
            });
          }
        }

        // Handle Cart Reset via destroyCartDB()
        if (cartResetFromAEM) {
          const storedCartResetVersion = localStorage.getItem(CART_RESET_KEY);
          if (storedCartResetVersion !== cartResetFromAEM) {
            await destroyCartDB();
            localStorage.setItem(CART_RESET_KEY, cartResetFromAEM);
          }
        }

        dispatch(setSceneContent(items));
      } catch (error) {
        console.error("Error processing scene content:", error);
      }
    };

    if (sceneContentData) {
      processSceneContent();
    }
  }, [sceneContentData, dispatch]);

  return (
    <>
      {mobileMedia && mobileMedia >= 1024 ? (
        <>
          <UserInfo headerData={headerData} />
          <UserPoints isDesktop={true} />
        </>
      ) : (
        <></>
      )}
      {isOfferLoaded && mulesoftPendingOffers.length > 0 && (
        <ToastComponent autoCloseDuration={900000}>
          <LoadedOffersToast mulesoftPendingOffers={mulesoftPendingOffers} />
        </ToastComponent>
      )}
    </>
  );
}
